import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageBanner } from "@/components/site/PageBanner";
import { SafeImage } from "@/components/site/SafeImage";
import { SectionHeading } from "@/components/site/SectionHeading";
import { api } from "@/lib/api";
import {
  DEVELOPERS,
  propertyMatchesDeveloper,
  type DeveloperContent,
} from "@/lib/developers";
import { DEVELOPER_LOGOS } from "@/lib/developer-logos";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import { useSeo } from "@/hooks/useSeo";
import type { Property } from "@/types/property";

export function DevelopersPage() {
  const { version } = useLiveProperties();
  const [items, setItems] = useState<Property[]>([]);

  useSeo({
    title: "Top Dubai Property Developers | Emaar, DAMAC, Sobha & More | Hive Homes",
    description:
      "Explore Dubai's leading developers — Emaar, DAMAC, Sobha, Ellington, Dubai Holding and Meraas. Browse each developer's live properties with Hive Homes.",
  });

  useEffect(() => {
    let cancelled = false;
    api
      .listProperties({ pageSize: 500 })
      .then((data) => !cancelled && setItems(data.items))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [version]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const developer of DEVELOPERS) {
      map.set(
        developer.slug,
        items.filter((p) => propertyMatchesDeveloper(p, developer)).length,
      );
    }
    return map;
  }, [items]);

  return (
    <>
      <PageBanner
        image="/images/site/communities.jpg"
        alt="Aerial view of a Dubai master-planned community with the city skyline behind"
      >
        <SectionHeading
          as="h1"
          eyebrow="Developer Partners"
          size="xl"
          title="Dubai's Top Developers."
          body="From master-planned lagoon communities to boutique design-led towers — every developer we work with, and everything they have on the market."
        />
      </PageBanner>

      <section className="container-wide py-10 md:py-14 short:py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[var(--line)] pb-5">
          <div>
            <p className="eyebrow">Directory</p>
            <h2
              className="mt-3 max-w-[18ch] font-display text-[30px] font-semibold leading-[1.03] tracking-[-0.02em] text-[var(--text)] sm:text-[38px] md:text-[46px]"
              style={{ fontVariationSettings: '"opsz" 72' }}
            >
              Explore All Developers.
            </h2>
          </div>
          <p className="meta uppercase tracking-[0.18em] text-[var(--text-muted)]">
            {String(DEVELOPERS.length).padStart(2, "0")} developers
          </p>
        </div>

        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 md:mt-10 md:gap-6">
          {DEVELOPERS.map((developer) => (
            <li key={developer.slug}>
              <DeveloperCard
                developer={developer}
                liveCount={counts.get(developer.slug) ?? 0}
              />
            </li>
          ))}
        </ul>
      </section>

      {/* No dead ends — the client's developer may not be listed yet. */}
      <section data-on-dark className="border-t border-[var(--line)] bg-[var(--navy-deep)]">
        <div className="container-wide flex flex-col items-start justify-between gap-6 py-12 md:flex-row md:items-center md:py-14 short:py-8">
          <div>
            <p className="eyebrow">Looking for Another Developer?</p>
            <h2
              className="mt-3 max-w-[24ch] font-display text-[26px] leading-[1.1] text-[var(--text-on-dark)] sm:text-[32px]"
              style={{ fontVariationSettings: '"opsz" 72' }}
            >
              We work across every major developer in Dubai.
            </h2>
          </div>
          <Link to="/contact" className="btn btn-primary btn-block-sm flex-none">
            Speak to an Advisor
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </Link>
        </div>
      </section>
    </>
  );
}

function DeveloperCard({
  developer,
  liveCount,
}: {
  developer: DeveloperContent;
  liveCount: number;
}) {
  const logo = DEVELOPER_LOGOS[developer.slug];
  return (
    <Link
      to={`/developers/${developer.slug}`}
      data-on-dark
      className="group relative isolate flex min-h-[280px] overflow-hidden border border-[rgba(211,197,150,0.18)] bg-[var(--navy)] shadow-[0_20px_60px_-46px_rgba(13,20,34,0.45)] sm:min-h-[300px]"
    >
      <SafeImage
        src={developer.cover}
        alt={developer.coverAlt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.82] transition-transform duration-[900ms] group-hover:scale-[1.04]"
      />
      <span
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(13,20,34,0.28)_0%,rgba(13,20,34,0.2)_36%,rgba(13,20,34,0.9)_100%)]"
      />

      {logo && (
        <span className="absolute left-4 top-4 inline-flex items-center bg-white px-3 py-2">
          <img
            src={logo.src}
            alt=""
            aria-hidden
            loading="lazy"
            className={`w-auto max-w-[130px] object-contain ${logo.className}`}
          />
        </span>
      )}

      <span className="relative flex min-h-full w-full flex-col justify-end p-5">
        <span
          translate="no"
          className="font-display text-[26px] leading-[1.05] text-[var(--text-on-dark)] transition-colors duration-200 group-hover:text-[var(--gold-bright)] sm:text-[28px]"
          style={{ fontVariationSettings: '"opsz" 72' }}
        >
          {developer.name}
        </span>
        <span className="mt-2 block max-w-[40ch] text-[13px] leading-[1.55] text-[var(--cream)]/90">
          {developer.blurb}
        </span>
        <span className="mt-4 flex items-center justify-between gap-3 border-t border-[rgba(211,197,150,0.24)] pt-3">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--cream)]">
            {liveCount > 0
              ? `${String(liveCount).padStart(2, "0")} Live ${liveCount === 1 ? "Listing" : "Listings"}`
              : "Ask Hive Homes"}
          </span>
          <svg
            className="size-5 flex-none text-[var(--cream)] transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[var(--gold-bright)]"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden
          >
            <path d="M4 10h12m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        </span>
      </span>
    </Link>
  );
}
